/**
 * @file    QuestionsCalculationsSection.tsx
 * @scope   VIEW
 * @summary Question and calculation pickers of the main view, plus their validation messages.
 * @consumes MultipleSelectSortable, GroupSelectSortable, rulesHelperTexts
 * @produces JSX only
 * @sideEffects None — selection and drag events are propagated through callbacks.
 * @notes LEGACY-ONLY: Enterprise V2 and Medusa-query widgets pick their measures elsewhere, so the
 *        caller gates this section with `!isDiyWidgetEngine && !hasMedusaQuery`.
 */
import React from 'react';
import { Skeleton, SkeletonVariants } from '@cx/ui';
import MultipleSelectSortable from 'core-ui/MultipleSelectSortable/MultipleSelectSortable';
import GroupSelectSortable from 'core-ui/GroupSelectSortable/GroupSelectSortable';
import { helperTextByCalculation, helperTextByQuestion } from 'helpers/rulesHelperTexts';

/** Message shown when the chosen question/calculation mix is rejected by the chart rules. */
const INVALID_COMBINATION_MESSAGE = 'This combination is not allowed with the specified chart';

export interface QuestionsCalculationsSectionProps {
  loading: boolean;
  /** Rules applicable to the current chart; drive the helper texts. */
  applicableRules: any[];
  /** Marks the pickers as touched so validation messages may appear. */
  onTouch: () => void;

  showQuestions: boolean;
  questionOptions: any;
  selectedQuestions: any;
  onQuestionChange: (value: any) => void;
  onQuestionsDragEnd: (event: any) => void;
  /** True when the current question selection breaks the chart rules. */
  questionsInvalid: boolean;

  showCalculations: boolean;
  calculationOptions: any;
  selectedCalculations: any;
  onCalculationChange: (value: any) => void;
  onCalculationsDragEnd: (event: any) => void;
  /** True when the current calculation selection breaks the chart rules. */
  calculationsInvalid: boolean;
}

const renderSkeleton = () => (
  <Skeleton variant={SkeletonVariants.rectangular} className="mainview_Skeleton" />
);

const QuestionsCalculationsSection: React.FC<QuestionsCalculationsSectionProps> = ({
  loading,
  applicableRules,
  onTouch,
  showQuestions,
  questionOptions,
  selectedQuestions,
  onQuestionChange,
  onQuestionsDragEnd,
  questionsInvalid,
  showCalculations,
  calculationOptions,
  selectedCalculations,
  onCalculationChange,
  onCalculationsDragEnd,
  calculationsInvalid,
}) => {
  // WHY two placeholders: the show flags depend on the catalog being loaded, so while it loads we
  // reserve the space of both pickers instead of letting them pop in.
  if (loading) {
    return (
      <>
        {renderSkeleton()}
        {renderSkeleton()}
      </>
    );
  }

  return (
    <>
      {showQuestions ? (
        <MultipleSelectSortable
          data-testid="question-selector"
          loading={loading}
          handleDragEnd={onQuestionsDragEnd}
          options={questionOptions}
          selectedChange={onQuestionChange}
          selectedValues={selectedQuestions}
          label="Questions"
          onBlurEvent={onTouch}
          helperText={helperTextByQuestion(applicableRules)}
        />
      ) : null}
      {showQuestions && questionsInvalid ? (
        <span className="diy-required-field">{INVALID_COMBINATION_MESSAGE}</span>
      ) : null}
      {showCalculations ? (
        <GroupSelectSortable
          loading={loading}
          handleDragEnd={onCalculationsDragEnd}
          options={calculationOptions}
          selectedChange={onCalculationChange}
          selectedValues={selectedCalculations}
          label="Calculations"
          onBlurEvent={onTouch}
          helperText={helperTextByCalculation(applicableRules)}
        />
      ) : null}
      {showCalculations && calculationsInvalid ? (
        <span className="diy-required-field">{INVALID_COMBINATION_MESSAGE}</span>
      ) : null}
    </>
  );
};

export default React.memo(QuestionsCalculationsSection);
